import React, { useState } from 'react';

import { LocalHistoryEditor, Queries } from '@aline/common';
import type { DrawErrorInstruction } from '@aline/common';
import { ButtonBase, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { Transforms } from 'slate';
import type { Path } from 'slate';
import { AlineButton } from 'src/components';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { useEditor, useSlateSelector } from '../hooks';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },

  button: {
    alignItems: 'center',
    backgroundColor: theme.appColors.defaultFieldBackground,
    border: '1px solid',
    borderColor: theme.appColors.reddish,
    borderRadius: 6,
    color: theme.appColors.reddish,
    display: 'flex',
    fontSize: 16,
    gap: theme.spacing(0.5),
    justifyContent: 'center',
    position: 'absolute',
  },

  buttonIcon: {
    '& > path': {
      fill: theme.appColors.reddish,
    },

    maxHeight: '75%',
    maxWidth: '75%',
  },

  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
  },

  header: {
    '& *': { color: 'white' },
    alignItems: 'center',
    backgroundColor: theme.appColors.reddish,
    display: 'flex',
    fontSize: 12,
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },

  headerIcon: {
    '& > path': { fill: 'white' },
    height: 18,
  },

  paper: {
    borderRadius: 6,
    boxShadow: theme.appShadows.popper,
    cursor: 'default',
    margin: theme.spacing(1),
    maxWidth: 500,
    overflow: 'hidden',
    width: '100%',
    zIndex: theme.zIndex.modal,
  },

  summary: {
    cursor: 'pointer',
  },

  textarea: {
    boxSizing: 'border-box',
    cursor: 'text',
    resize: 'none',
    width: '100%',
  },
}));

export function RenderingError({ height, rMPath, width, x, y }: DrawErrorInstruction) {
  const editor = useEditor();
  const editingDisabled = useEditingDisabled();
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const canUndo = useSlateSelector(
    editor => LocalHistoryEditor.isYHistoryEditor(editor) && LocalHistoryEditor.canUndo(editor)
  );

  let cMPath: Path | null = null;
  let elementText: string | null = null;

  /**
   * Since we know there's something wrong with this element, be extra careful to avoid another
   * error.
   */
  try {
    cMPath = Queries.getCMPathFromRMPath(editor, rMPath);
    elementText = Queries.string(editor, { at: cMPath });
  } catch (error) {
    console.error(error);
  }

  function handleDelete() {
    if (!cMPath) return;
    Transforms.removeNodes(editor, { at: cMPath });
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div
        onContextMenu={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
      >
        <ButtonBase
          ref={setAnchorEl}
          aria-label="Something went wrong"
          className={classes.button}
          style={{ height, left: x, top: y, width }}
          onClick={() => setOpen(!open)}
        >
          <WarningIcon className={classes.buttonIcon} />

          {width >= 350 && 'Something went wrong'}
        </ButtonBase>
        <Popper disablePortal anchorEl={anchorEl} open={open}>
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <WarningIcon className={classes.headerIcon} />
              <Typography>Could not render element</Typography>
            </div>

            <div className={classes.body}>
              <Typography>A problem occurred while attempting to render this element.</Typography>

              {!editingDisabled && (
                <Typography
                  sx={{
                    fontSize: 12,
                  }}
                >
                  Undoing the last change might fix the problem, or you can delete the element.
                  Contact support if the issue persists.
                </Typography>
              )}

              {elementText && (
                <details>
                  <summary className={classes.summary}>View element text</summary>
                  <textarea disabled className={classes.textarea} value={elementText} />
                  <Typography
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    Copy this text and paste it in a safe place before deleting this element.
                  </Typography>
                </details>
              )}

              {!editingDisabled && (
                <div className={classes.buttons}>
                  <AlineButton
                    color="primary"
                    disabled={!canUndo}
                    variant="outlined"
                    onClick={editor.undo}
                  >
                    Undo last change
                  </AlineButton>

                  <AlineButton
                    color="error"
                    disabled={!cMPath}
                    variant="outlined"
                    onClick={handleDelete}
                  >
                    Delete element
                  </AlineButton>
                </div>
              )}
            </div>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}

function stopPropagation(event: React.MouseEvent) {
  event.stopPropagation();
}
